/* eslint-disable react/no-unescaped-entities */
import React, { memo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';

import Container from 'components/Container';
import { Button, Grid2, SxProps, Theme, useMediaQuery } from '@mui/material';

// 提取常用样式
const styles = {
  imageWrapper: {
    maxWidth: 465,
    position: 'relative',
    marginX: 'auto',
  } as const,
  image: {
    objectFit: 'cover',
    width: 1,
    height: 1,
  } as const,
  listItem: {
    width: 'auto',
    padding: 0,
  } as const,
  avatar: {
    bgcolor: '#2563EB',
    width: 20,
    height: 20,
  } as const,
} as const;

// 提取 SVG 图标组件
const CheckIcon = memo(() => (
  <svg
    width={12}
    height={12}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    role="img"
    aria-label="check mark"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
));

CheckIcon.displayName = 'CheckIcon';

interface Feature {
  key: string;
  text: string; // 改为直接使用文本而不是翻译key
}

interface FeaturesWithMobileScreenshotProps {
  imageSrc?: string;
  imageAlt?: string;
  features?: Feature[];
  title?: string;
  subTitle?: string;
  buttonText?: string;
  buttonLink?: string;
  imageWrapperSx?: SxProps<Theme>;
  imageSx?: SxProps<Theme>;
  imagePosition?: 'left' | 'right'; // 新增属性
}

const FeaturesWithMobileScreenshot = ({
  imageSrc = '/images/others/eor-image3.png',
  imageAlt = 'Image Description',
  features,
  title = 'Built for Ambitious Businesses Like Yours',
  subTitle = 'Transform your global expansion strategy',
  buttonText = '预约演示',
  buttonLink = '/book-demo',
  imageWrapperSx,
  imageSx,
  imagePosition = 'left', // 默认左图右文
}: FeaturesWithMobileScreenshotProps): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const ImageSection = (
    <Grid2 size={{ xs: 12, md: 6 }}>
      <Box sx={{ ...styles.imageWrapper, ...imageWrapperSx }}>
        <Box
          component={'img'}
          src={imageSrc}
          alt={imageAlt}
          sx={{
            ...styles.image,
            ...imageSx,
            filter: theme.palette.mode === 'dark' ? 'brightness(0.7)' : 'none',
          }}
        />
      </Box>
    </Grid2>
  );

  const ContentSection = (
    <Grid2
      size={{ xs: 12, md: 6 }}
      display="flex"
      alignItems="center"
      component="section"
      aria-labelledby="features-title"
    >
      <Box>
        <Box marginBottom={2}>
          <Typography
            variant={'h4'}
            sx={{ fontWeight: 700 }}
            gutterBottom
            id="features-title"
          >
            {title}
          </Typography>
          <Typography
            color="text.secondary"
            // variant={'h6'}
            fontSize={14}
          >
            {subTitle}
            <br />
          </Typography>
        </Box>
        {features && (
          <Grid2
            container
            spacing={1}
            component="ul"
            sx={{ listStyle: 'none', p: 0 }}
          >
            {features.map((feature) => (
              <Grid2 size={{ xs: 12 }} key={feature.key} component="li">
                <Box component={ListItem} disableGutters sx={styles.listItem}>
                  <Box
                    component={ListItemAvatar}
                    minWidth={'auto !important'}
                    marginRight={2}
                  >
                    <Box component={Avatar} sx={styles.avatar}>
                      <CheckIcon />
                    </Box>
                  </Box>
                  <ListItemText primary={feature.text} />
                </Box>
              </Grid2>
            ))}
          </Grid2>
        )}
        <Grid2 size={{ xs: 12 }} marginTop={4}>
          <Button
            size={'large'}
            variant={'contained'}
            fullWidth={isMd ? false : true}
            href={buttonLink}
            aria-label={buttonText}
          >
            {buttonText}
          </Button>
        </Grid2>
      </Box>
    </Grid2>
  );

  return (
    <Container>
      <Grid2
        container
        spacing={4}
        direction={{
          xs: 'column',
          md: imagePosition === 'left' ? 'row' : 'row-reverse',
        }}
      >
        {ImageSection}
        {ContentSection}
      </Grid2>
    </Container>
  );
};

export default memo(FeaturesWithMobileScreenshot);
